import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ThemedText } from '@/components/themed-text';
import { cn } from '@/utils/cn';
import { formatCurrency } from '@/utils/format-currency';
import { Ionicons } from '@expo/vector-icons';
import React, { useEffect, useMemo, useState } from 'react';
import { View } from 'react-native';
import { router } from 'expo-router';
import { CHARGE_STATUS } from '@/types/charge-status';
import {
  format,
  subDays,
  startOfDay,
  endOfDay,
  startOfMonth,
  endOfMonth,
  addMonths,
} from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useProviderCharges } from '@/hooks/use-provider-charges';
import { ErrorState } from '@/components/ui/error-state';
import { EmptyState } from '@/components/ui/empty-state';
import { FadeInView } from '@/components/ui/fade-in-view';
import { FilterBar } from '@/components/ui/filter-chips';
import { StatusBadge } from '@/components/ui/status-badge';
import { SkeletonListRow } from '@/components/ui/skeleton';
import { ManualChargeModal } from '@/components/charges/manual-charge-modal';
import { ChargeDetailModal } from '@/components/enrollments/charge-detail-modal';
import { ChargesAutomaticBanner } from '@/components/payments/charges-automatic-banner';
import { Pagination } from '@/components/ui/pagination';
import { PageLayout } from '@/components/ui/page-layout';
import { PressableScale } from '@/components/ui/pressable-scale';
import { Period, PeriodSelector } from '@/components/dashboard/period-selector';
import { useCharge } from '@/hooks/use-charges';
import { useFocusParam } from '@/hooks/use-focus-param';
import { useMembership } from '@/hooks/use-membership';
import { useTeamPayments } from '@/hooks/use-team-payments';
import { useTerms } from '@/hooks/use-terms';
import { FOCUS_PARAM } from '@/lib/notification-navigation';
import { PROCESSING_METHOD } from '@/types/processing-method';
import { useThemedHex, useSolidChip } from '@/hooks/use-theme-colors';

type FilterStatus = 'all' | CHARGE_STATUS;

const DEFAULT_ITEMS_PER_PAGE = 10;

const STATUS_FILTERS: { label: string; value: FilterStatus }[] = [
  { value: 'all', label: 'Todas' },
  { value: CHARGE_STATUS.PENDING, label: 'Pendentes' },
  { value: CHARGE_STATUS.PAID, label: 'Pagas' },
  { value: CHARGE_STATUS.OVERDUE, label: 'Vencidas' },
  { value: CHARGE_STATUS.IN_DISPUTE, label: 'Em disputa' },
  { value: CHARGE_STATUS.REFUNDED, label: 'Reembolsadas' },
];

const statusVisuals: Partial<Record<CHARGE_STATUS, { icon: string; color: string; bg: string }>> = {
  [CHARGE_STATUS.PAID]: { icon: 'arrow-down-circle', color: '#16A34A', bg: 'bg-success-50' },
  [CHARGE_STATUS.PENDING]: { icon: 'time-outline', color: '#D97706', bg: 'bg-warning-50' },
  [CHARGE_STATUS.OVERDUE]: { icon: 'alert-circle', color: '#DC2626', bg: 'bg-error-50' },
  [CHARGE_STATUS.REFUNDED]: { icon: 'arrow-back-circle', color: '#737373', bg: 'bg-neutral-100' },
  [CHARGE_STATUS.IN_DISPUTE]: { icon: 'warning', color: '#D97706', bg: 'bg-warning-50' },
};

const fallbackVisual = { icon: 'help-circle', color: '#737373', bg: 'bg-neutral-100' };

export default function TransactionsScreen() {
  const solid = useSolidChip();
  const th = useThemedHex();
  const [filterStatus, setFilterStatus] = useState<FilterStatus>('all');
  const [manualChargeOpen, setManualChargeOpen] = useState(false);
  const [selectedChargeId, setSelectedChargeId] = useState<string | null>(null);
  const [period, setPeriod] = useState<Period>('month');
  const [customRange, setCustomRange] = useState<{ start: Date; end: Date }>({
    start: startOfMonth(new Date()),
    end: endOfMonth(new Date()),
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_ITEMS_PER_PAGE);

  // Deep link de notificação: abre direto o detalhe da cobrança avisada.
  const [focusChargeId, clearFocusCharge] = useFocusParam(FOCUS_PARAM.charge);
  useEffect(() => {
    if (focusChargeId) setSelectedChargeId(focusChargeId);
  }, [focusChargeId]);

  const { startDate, endDate } = useMemo(() => {
    const now = new Date();
    let start: Date;
    let end: Date;
    if (period === 'custom') {
      start = startOfDay(customRange.start);
      end = endOfDay(customRange.end);
    } else if (period === 'nextMonth') {
      const next = addMonths(now, 1);
      start = startOfMonth(next);
      end = endOfMonth(next);
    } else if (period === 'month') {
      start = startOfMonth(now);
      end = endOfMonth(now);
    } else {
      const days = period === '7d' ? 7 : period === '30d' ? 30 : 90;
      end = endOfDay(now);
      start = startOfDay(subDays(end, days - 1));
    }
    return {
      startDate: format(start, 'yyyy-MM-dd'),
      endDate: format(end, 'yyyy-MM-dd'),
    };
  }, [period, customRange]);

  const {
    data: paginatedData,
    isLoading,
    error,
    refetch,
  } = useProviderCharges({
    status: filterStatus !== 'all' ? (filterStatus as CHARGE_STATUS) : undefined,
    startDate,
    endDate,
    page: currentPage,
    limit: itemsPerPage,
  });

  const charges = paginatedData?.data || [];
  const totalPages = paginatedData?.meta?.totalPages || 0;
  const periodTotal = paginatedData?.meta?.periodTotal;
  const totalCount = paginatedData?.meta?.total ?? 0;

  const { data: selectedCharge } = useCharge(selectedChargeId || undefined);

  const terms = useTerms();

  // Faixa "recebimento automático" (T4.5): só faz sentido contar quando a
  // lista já está no recorte "mês atual, todas" — fora disso o número não
  // bateria com "este mês". Conta só a página carregada (a API não dá uma
  // contagem própria do mês inteiro), limite documentado no componente.
  const manualConfirmedThisMonthCount =
    period === 'month' && filterStatus === 'all'
      ? charges.filter(
          (charge) =>
            charge.status === CHARGE_STATUS.PAID &&
            charge.processingMethod === PROCESSING_METHOD.MANUAL,
        ).length
      : 0;

  // Caixa único: o dono confere os pagamentos de toda a equipe numa tela só.
  const { can } = useMembership();
  const showTeamPayments = can('teamPayments');
  const { data: teamReported } = useTeamPayments({ status: 'PAYMENT_REPORTED', limit: 1 });
  const reportedCount = teamReported?.counts.reported.count ?? 0;

  const handleNewCharge = () => setManualChargeOpen(true);

  return (
    <>
      <PageLayout
        title="Cobranças"
        subtitle="Tudo o que entra e o que está por vir"
        buttonTitle={`Cobrar ${terms.client}`}
        onButtonPress={handleNewCharge}
        buttonTestID="charges-new-charge-btn"
      >
        {showTeamPayments && (
          <PressableScale
            haptic
            accessibilityRole="button"
            onPress={() => router.push('/(tabs)/(provider)/team/payments' as never)}
            className="flex-row items-center gap-3 rounded-2xl border border-neutral-900/5 bg-card p-4"
            style={{ borderCurve: 'continuous' }}
          >
            <View
              className={cn(
                'h-10 w-10 shrink-0 items-center justify-center rounded-full',
                reportedCount > 0 ? 'bg-warning-50 dark:bg-warning-500' : 'bg-neutral-100',
              )}
            >
              <Ionicons
                name="people-outline"
                size={18}
                className={reportedCount > 0 ? 'text-warning-600 dark:text-white' : 'text-neutral-500'}
              />
            </View>
            <View className="min-w-0 flex-1 gap-0.5">
              <ThemedText className="font-semibold text-sm text-foreground">
                Pagamentos da equipe
              </ThemedText>
              <ThemedText className="text-xs text-neutral-500" numberOfLines={1}>
                {reportedCount > 0
                  ? `${reportedCount} aguardando confirmação`
                  : 'Nada aguardando confirmação'}
              </ThemedText>
            </View>
            <Ionicons className="text-neutral-400" name="chevron-forward" size={16} />
          </PressableScale>
        )}

        <ChargesAutomaticBanner manualConfirmedThisMonthCount={manualConfirmedThisMonthCount} />

        {/* Filtros direto no fundo, sem card — cada linha diz o que filtra */}
        <View className="gap-3">
          <PeriodSelector
            noCard
            label="Período"
            period={period}
            onPeriodChange={(p) => {
              setPeriod(p);
              setCurrentPage(1);
            }}
            customRange={customRange}
            onCustomRangeChange={(range) => {
              setCustomRange(range);
              setCurrentPage(1);
            }}
            variants={['month', 'nextMonth', 'custom']}
          />
          <FilterBar
            label="Situação"
            options={STATUS_FILTERS}
            value={filterStatus}
            onChange={(value) => {
              setFilterStatus(value);
              setCurrentPage(1);
            }}
          />
        </View>

        {/* Resumo do período */}
        {periodTotal != null && filterStatus === 'all' && !isLoading && (
          <FadeInView delay={0}>
            <View
              className="flex-row items-center justify-between rounded-2xl bg-ink px-4 py-3.5"
              style={{ borderCurve: 'continuous' }}
            >
              <ThemedText className="font-medium text-sm text-white/60">
                Total no período
              </ThemedText>
              <ThemedText
                className="font-bold text-lg text-white"
                style={{ fontVariant: ['tabular-nums'] }}
              >
                {formatCurrency(periodTotal)}
              </ThemedText>
            </View>
          </FadeInView>
        )}

        {/* Lista */}
        <Card className="w-full">
          <CardHeader>
            <CardTitle>
              {filterStatus === 'all'
                ? 'Todas as cobranças'
                : STATUS_FILTERS.find((f) => f.value === filterStatus)?.label}
            </CardTitle>
            {totalCount > 0 && (
              <ThemedText className="text-xs text-neutral-500">
                {totalCount} {totalCount === 1 ? 'cobrança' : 'cobranças'}
              </ThemedText>
            )}
          </CardHeader>

          <CardContent className="w-full pt-0">
            {isLoading ? (
              <View className="py-2">
                {Array.from({ length: 5 }).map((_, index) => (
                  <SkeletonListRow key={index} />
                ))}
              </View>
            ) : error ? (
              <ErrorState
                variant="inline"
                title="Erro ao carregar cobranças"
                message="Não foi possível carregar a lista de cobranças."
                onRetry={refetch}
              />
            ) : charges.length === 0 ? (
              <EmptyState
                icon="receipt-outline"
                title="Nenhuma cobrança encontrada"
                description={
                  filterStatus !== 'all'
                    ? 'Não há cobranças com este status no período.'
                    : `Crie uma cobrança manual para ${terms.agree('um', 'enrollment')} ${terms.enrollment} existente.`
                }
                actionLabel="Nova cobrança"
                onAction={handleNewCharge}
                className="py-10"
              />
            ) : (
              <View className="w-full">
                {charges.map((charge, index) => {
                  const visual = statusVisuals[charge.status] ?? fallbackVisual;
                  const clientName = charge.clientName ?? charge.enrollment?.client?.user?.username;
                  const serviceName = charge.serviceName ?? charge.enrollment?.service?.name;
                  const secondaryLabel = charge.description || serviceName;
                  const dateLabel = charge.paidAt
                    ? `Pago em ${format(new Date(charge.paidAt), 'dd/MM/yy', { locale: ptBR })}`
                    : `Vence ${format(new Date(charge.dueDate), 'dd/MM/yy', { locale: ptBR })}`;

                  return (
                    <FadeInView key={charge.id} delay={index * 30}>
                      <PressableScale
                        haptic
                        accessibilityRole="button"
                        onPress={() => setSelectedChargeId(charge.id)}
                        className={cn(
                          'flex-row items-center gap-3 py-3.5',
                          index < charges.length - 1 && 'border-b border-neutral-100',
                        )}
                      >
                        <View
                          className={cn(
                            'h-10 w-10 shrink-0 items-center justify-center rounded-full',
                            solid.cls(visual.bg),
                          )}
                          style={solid.style(visual.color)}
                        >
                          <Ionicons name={visual.icon as any} size={18} color={solid.icon(visual.color)} />
                        </View>

                        <View className="min-w-0 flex-1 gap-0.5">
                          <ThemedText
                            className="font-semibold text-sm leading-tight text-foreground"
                            numberOfLines={1}
                          >
                            {clientName || (charge.isManual ? 'Cobrança manual' : 'Cobrança')}
                          </ThemedText>
                          {secondaryLabel ? (
                            <ThemedText className="text-xs text-neutral-500" numberOfLines={1}>
                              {secondaryLabel}
                            </ThemedText>
                          ) : null}
                          <ThemedText className="text-xs text-neutral-400">{dateLabel}</ThemedText>
                        </View>

                        <View className="shrink-0 flex-row items-center gap-2">
                          <View className="items-end gap-1">
                            <ThemedText
                              className="font-bold text-sm text-foreground"
                              style={{ fontVariant: ['tabular-nums'] }}
                            >
                              {formatCurrency(charge.amount)}
                            </ThemedText>
                            <StatusBadge
                              status={{ type: 'charge', value: charge.status }}
                              size="xs"
                            />
                          </View>
                          <Ionicons className="text-neutral-400" name="chevron-forward" size={16} />
                        </View>
                      </PressableScale>
                    </FadeInView>
                  );
                })}
              </View>
            )}
          </CardContent>
        </Card>

        {totalCount > 0 && !isLoading && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages || 1}
            onPageChange={setCurrentPage}
            itemsPerPage={itemsPerPage}
            onItemsPerPageChange={setItemsPerPage}
            itemsPerPageOptions={[10, 20, 50, 100]}
          />
        )}
      </PageLayout>

      <ChargeDetailModal
        visible={!!selectedChargeId}
        charge={selectedCharge || null}
        onClose={() => {
          setSelectedChargeId(null);
          clearFocusCharge();
        }}
        onUpdate={() => {
          refetch();
          setSelectedChargeId(null);
          clearFocusCharge();
        }}
      />

      <ManualChargeModal
        open={manualChargeOpen}
        onOpenChange={setManualChargeOpen}
        onSuccess={() => refetch()}
      />
    </>
  );
}
